import { type OptionItem } from "@common/declarations";
import { findAndRemove, insert } from "@common/lib/array";

export type TreeNodeID = string | number;

export type NodeBase = {
  children?: NodeBase[];
};

export interface TreeNode {
  id: TreeNodeID;
  parentId?: TreeNodeID | null;
}

export type TreeNodeWithChildren<T = any> = TreeNode & {
  children?: T[];
};

const _buildTree = <T extends TreeNode, R extends T & TreeNodeWithChildren>(
  parent: R,
  items: T[],
  processChildren?: (items: T[]) => R[]
) => {
  parent.children = items.filter((x) => x.parentId == parent.id);

  parent.children = parent.children.length && processChildren ? processChildren(parent.children) : parent.children;

  parent.children.forEach((x) => _buildTree(x, items, processChildren));
};

export const buildTree = <T extends TreeNode>(
  items: T[],
  process?: (items: NodeWithChildren<T>[]) => NodeWithChildren<T>[]
): NodeWithChildren<T>[] => {
  let result = items.filter((x) => !x.parentId).map<NodeWithChildren<T>>((x) => ({ ...x, children: [] } as any));

  if (process) {
    result = process(result);
  }

  result.forEach((x) => _buildTree(x, items, process));

  return result;
};

export type TreeOptionNode<T = any> = OptionItem<T>;

export const traverseOptionTree = <T extends TreeOptionNode>(
  node: T,
  enter: (parent: T | null, child: T) => any,
  bottomUp: boolean = false
): void => {
  node.children &&
    node.children.forEach((child: T) => {
      !bottomUp && enter(node, child);

      traverseOptionTree(child, enter, bottomUp);

      bottomUp && enter(node, child);
    });
};

export const traverseTreeBase = <T>(
  node: T,
  getChildren: (item: T) => T[] | undefined,
  enter?: (parent: T | null, child: T, level: number) => any,
  exit?: (parent: T | null, child: T, level: number) => any
): void => {
  const traverse = (node: T, level: number) => {
    const children = getChildren(node);
    children &&
      children.forEach((child: T) => {
        enter && enter(node, child, level);

        traverse(child, level + 1);

        exit && exit(node, child, level);
      });
  };

  traverse(node, 1);
};
export const traverseTree = <T extends NodeBase>(
  node: T,
  enter?: (parent: T | null, child: T, level: number) => any,
  exit?: (parent: T | null, child: T, level: number) => any
): void => traverseTreeBase(node, (x) => x.children as T[], enter, exit);

export const traverseNodes = <T extends NodeBase>(
  nodes: T[],
  enter?: (parent: T | null, child: T) => any,
  exit?: (parent: T | null, child: T) => any
): void => {
  for (let i = 0; i < nodes.length; i++) {
    enter && enter(null, nodes[i]);

    traverseTree(nodes[i], enter, exit);

    exit && exit(null, nodes[i]);
  }
};

export const countNodes = <T extends NodeBase>(items: T[], isMatch?: (node: T, level: number) => boolean): number => {
  let count = 0;

  const root: T = {
    label: "",
    value: null,
    children: items
  } as any;
  traverseTree(root, (_, child, level) => (!isMatch || isMatch(child, level)) && count++);
  return count;
};

type NodeWithChildren<T> = T & {
  children?: NodeWithChildren<T>[];
};
const _nodesToPlain = <T extends TreeNode, R = T>(
  parent: R | null,
  children: NodeWithChildren<T>[],
  mapChildren: (parent: R | null, children: T[]) => R[]
): R[] => {
  return children.reduce(
    (res, x, i) => [...res, ..._nodesToPlain(res[i], x.children ?? [], mapChildren)],
    mapChildren(parent, children)
  );
};
export const nodesToPlain = <T extends TreeNode, R = T>(
  nodes: NodeWithChildren<T>[],
  mapChildren?: (parent: R | null, children: T[]) => R[]
): R[] => {
  const mapFn =
    mapChildren ??
    ((parent: R | null, children: T[]) => children.map((x) => ({ ...x, parentId: parent ? parent["id"] : null } as R)));

  return _nodesToPlain(null, nodes, mapFn);
};

export const findInNodesBase = <T>(
  items: T[],
  getChildren: (item: T) => T[] | undefined,
  isMatch: (node: T) => boolean
): T | undefined => {
  const root: T = {} as any;
  return findInTreeBase(root, (x) => (x === root ? items : getChildren(x)), isMatch);
};
export const findInNodes = <T extends NodeBase>(items: T[], isMatch: (node: T) => boolean): T | undefined =>
  findInNodesBase(items, (x) => x.children as T[], isMatch);

export const findInTreeBase = <T>(
  root: T,
  getChildren: (node: T) => T[] | undefined,
  isMatch: (node: T) => boolean
): T | undefined => {
  let node: T | undefined;
  traverseTreeBase(root, getChildren, (_, child) => !node && isMatch(child) && (node = child));
  return node;
};
export const findInTree = <T extends NodeBase>(root: T, isMatch: (node: T) => boolean): T | undefined =>
  findInTreeBase(root, (x) => x.children as T[], isMatch);

export const filterTree = <T extends NodeBase>(root: T, isMatch: (node: T) => boolean): T => {
  if (!root.children) return root;

  return {
    ...root,
    children: root.children.filter(isMatch).map((child) => filterTree(child, isMatch))
  };
};

/**
 * @param strategy
 *   root - any children of a root node won't be returned, if the root node does not match the condition
 *   child - any node that match the condition will be returned
 *   childIncludeParents - similar to child, but include all parents of matching items
 */
export const filterNodes = <T extends NodeBase>(
  nodes: T[],
  isMatch: (node: T) => boolean,
  strategy: "root" | "child" | "childIncludeParents" = "root"
): T[] => {
  if (strategy === "root") {
    const result = nodes.filter(isMatch);
    return result.map((node) => filterTree(node, isMatch));
  }

  if (strategy === "child") {
    const result: T[] = [];
    const queue: T[] = nodes.slice(0);
    while (queue.length) {
      const node: T = queue.shift()!;
      if (isMatch(node)) {
        result.push(filterTree<T>(node, isMatch));
      } else {
        queue.push(...((node.children ?? []) as T[]));
      }
    }
    return result;
  }

  const getNodes = (result: T[], node: T) => {
    // If current node matches, include it with all children
    if (isMatch(node)) {
      result.push(node);
      return result;
    }

    // If node has children, recursively filter them
    if (Array.isArray(node.children)) {
      const children = node.children.reduce(getNodes, []);
      // If any children match, include this parent with filtered children
      if (children.length) {
        result.push({ ...node, children });
      }
    }

    return result;
  };

  return nodes.reduce(getNodes, []);
};

const _flattenOptions = <T extends OptionItem<any>>(items: T[], result: T[]): T[] =>
  items.reduce((res, item) => [...res, item, ..._flattenOptions((item.children as T[]) || [], [])], result);

export const flattenOptions = <T extends OptionItem<any>>(items: T[]): T[] => _flattenOptions(items, []);

const _mapTree = <T extends TreeNodeWithChildren, R extends TreeNodeWithChildren>(
  parent: R | null,
  node: TreeNodeWithChildren,
  map: (parent: R | null, node: TreeNodeWithChildren) => R
): R => {
  let newNode = map(parent, node);
  newNode.children = node.children ? node.children.map((child) => _mapTree<T, R>(newNode, child, map)) : [];
  return newNode;
};

export const mapTree = <T extends TreeNodeWithChildren, R extends TreeNodeWithChildren>(
  node: TreeNodeWithChildren,
  map: (parent: R, node: T) => R
): R => _mapTree(null, node, map);

export const mapNodes = <T extends TreeNode, R extends TreeNodeWithChildren = TreeNodeWithChildren>(
  nodes: TreeNodeWithChildren[],
  map: (parent: R, node: T) => R
): R[] => nodes.map((n) => mapTree(n, map as any));

const _mapTreeUp = <T extends TreeNodeWithChildren, R extends TreeNodeWithChildren>(
  node: T,
  map: (node: T, children: R[]) => R
): R => {
  let children = (node.children || []).map((child) => _mapTreeUp(child, map));
  return {
    ...map(node, children),
    children
  };
};

export const mapTreeUp = <T extends TreeNodeWithChildren, R extends TreeNodeWithChildren>(
  node: T,
  map: (node: T, children: R[]) => R
): R => _mapTreeUp(node, map);

export const mapNodesUp = <T extends TreeNodeWithChildren, R extends TreeNodeWithChildren = T>(
  nodes: T[],
  map: (node: T, children: R[]) => R
): R[] => nodes.map((n) => mapTreeUp(n, map));

// https://github.com/teleport/react-dnd-treeview
const recursivelyUpdateNode = <T extends TreeNodeWithChildren>(
  node: T,
  listUpdateFunc: (list: T[], parentNode: T | null) => T[],
  nodeUpdateFunc: (node: T) => T
) => {
  const updateChildren = node.children
    ? recursivelyUpdateNodes(node.children, node, listUpdateFunc, nodeUpdateFunc)
    : node.children;
  if (updateChildren !== node.children && (updateChildren?.length || node.children?.length)) {
    node = {
      ...node,
      children: updateChildren
    };
  }
  return nodeUpdateFunc(node);
};

export const recursivelyUpdateNodes = <T extends TreeNodeWithChildren>(
  list: T[],
  parentNode: T | null,
  listUpdateFunc: (list: T[], parentNode: T | null) => T[],
  nodeUpdateFunc: (node: T) => T
): T[] => {
  const mappedItems = list.map((item) => recursivelyUpdateNode(item, listUpdateFunc, nodeUpdateFunc));
  return listUpdateFunc(mappedItems, parentNode);
};

/**
 * Does not modify the source
 */
export const cloneNode = <T extends TreeNodeWithChildren>(nodes: T[], id: TreeNodeID, cloneFn: (node: T) => T): T[] =>
  recursivelyUpdateNodes(
    nodes,
    null,
    (list: T[], parentNode: T): T[] => {
      const index = list.findIndex((n) => n.id === id);
      if (index >= 0) {
        return insert(list, cloneFn(list[index]), index + 1);
      }
      return list;
    },
    (item) => item
  );

/**
 * Does not modify the source
 */
export const deleteNode = <T extends TreeNodeWithChildren>(nodes: T[], id: TreeNodeID): T[] =>
  recursivelyUpdateNodes(
    nodes,
    null,
    (list: T[], parentNode: T): T[] => findAndRemove(list, (node) => node.id === id),
    (item) => item
  );

export const isSameNode = (a: TreeNode | null, b: TreeNode | null) => (!a && !b) || (a && b && a.id === b.id);

/**
 * Does not modify the source
 */
export const moveNode = <T extends TreeNodeWithChildren>(
  nodes: T[],
  id: TreeNodeID,
  newParentId: TreeNodeID | null,
  index: number
): T[] => {
  const node = findInNodes(nodes, (x) => x.id === id);

  let result = deleteNode(nodes, id);
  if (!newParentId) {
    return insert(result, node, index);
  }

  return updateNode(result, newParentId, (x) => ({ ...x, children: insert(x.children ?? [], node, index) }));
};

/**
 * Does not modify the source
 */
export const updateNode = <T extends TreeNodeWithChildren>(nodes: T[], id: TreeNodeID, updateFn: (node: T) => T): T[] =>
  recursivelyUpdateNodes(
    nodes,
    null,
    (list: T[], parentNode: T): T[] => list,
    (item) => (item.id === id ? updateFn(item) : item)
  );

export const updateNodeTree = <T extends TreeNodeWithChildren>(
  nodes: T[],
  id: TreeNodeID,
  updateFn: (node: T) => T
): T[] => {
  const mapRecursive = (node: T): T => {
    node.children = node.children?.map(mapRecursive);
    return updateFn(node);
  };

  const traverse = (node: T): T => {
    if (node.id === id) return mapRecursive(node);

    node.children = node.children?.map(traverse);
    return node;
  };

  return nodes.map(traverse);
};
